import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import LogoLoader from './components/LogoLoader';

// Layout
import Layout from './components/layout/Layout';

// Pages
import Login from './pages/Login';
import MainMenu from './pages/MainMenu';
import Dashboard from './pages/Dashboard';
import Receivables from './pages/Receivables';
import LabPlanner from './pages/LabPlanner';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Factories from './pages/Factories';
import SalesDashboard from './pages/SalesDashboard';
import Settings from './pages/Settings';
import Users from './pages/Users';
import CustomerTrack from './pages/CustomerTrack';
import FactoryDashboard from './pages/FactoryDashboard';
import FactoryOrderDetail from './pages/FactoryOrderDetail';
import Quotations from './pages/Quotations';
import QuotationDetail from './pages/QuotationDetail';
import QuotationRequests from './pages/QuotationRequests';
import QuotationRequestDetail from './pages/QuotationRequestDetail';
import Timeline from './pages/Timeline';
import MetalLedger from './pages/MetalLedger';
import Invoices from './pages/Invoices';
import ChainInventory from './pages/ChainInventory';
import ChainPurchaseOrders from './pages/ChainPurchaseOrders';
import Portal from './pages/Portal';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import MyDesignTasks from './pages/MyDesignTasks';
import MouldTracker from './pages/MouldTracker';
import Exhibitions from './pages/Exhibitions';
import ExhibitionDetail from './pages/ExhibitionDetail';
import Diamonds from './pages/Diamonds';

// Protected Route wrapper
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-neutral-950">
        <LogoLoader size="lg" text="Loading..." />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Factory users go to factory portal
  if (user.role === 'factory' && !allowedRoles?.includes('factory')) {
    return <Navigate to="/factory" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route wrapper (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-neutral-950">
        <LogoLoader size="lg" />
      </div>
    );
  }

  if (user) {
    // Factory users go to factory portal
    if (user.role === 'factory') {
      return <Navigate to="/factory" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public customer tracking route - no auth required */}
      <Route path="/track" element={<CustomerTrack />} />
      
      {/* Customer Portal - separate auth */}
      <Route path="/portal" element={<Portal />} />
      
      {/* Public routes */}
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />

      {/* Factory Portal routes */}
      <Route 
        path="/factory" 
        element={
          <ProtectedRoute allowedRoles={['factory']}>
            <FactoryDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/factory/orders/:id" 
        element={
          <ProtectedRoute allowedRoles={['factory']}>
            <FactoryOrderDetail />
          </ProtectedRoute>
        } 
      />

      {/* Protected routes with Layout (internal team only) */}
      <Route 
        element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'production', 'sales', 'finance', 'designer']}>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<MainMenu />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/my-design-tasks" element={<MyDesignTasks />} />
        <Route path="/mould-tracker" element={<MouldTracker />} />
        <Route path="/receivables" element={<Receivables />} />
        <Route path="/lab-planner" element={<LabPlanner />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/orders/:id" element={<OrderDetail />} />
        <Route path="/quotations" element={<Quotations />} />
        <Route path="/quotations/:id" element={<QuotationDetail />} />
        <Route path="/quotation-requests" element={<QuotationRequests />} />
        <Route path="/quotation-requests/:id" element={<QuotationRequestDetail />} />
        <Route path="/timeline" element={<Timeline />} />
        <Route path="/metal-ledger" element={<MetalLedger />} />
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/chains" element={<ChainInventory />} />
        <Route path="/chain-pos" element={<ChainPurchaseOrders />} />
        <Route path="/diamonds" element={<Diamonds />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/customers/:customerId" element={<CustomerDetail />} />
        <Route path="/exhibitions" element={<Exhibitions />} />
        <Route path="/exhibitions/:id" element={<ExhibitionDetail />} />
        <Route 
          path="/factories" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin', 'production']}>
              <Factories />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/sales" 
          element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <SalesDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <Settings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/users" 
          element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <Users />
            </ProtectedRoute>
          } 
        />
      </Route>

      {/* Catch all redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            richColors 
            toastOptions={{
              className: 'font-body',
              duration: 3000,
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
